import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { format, addDays, parse, isBefore, parseISO } from "date-fns";
import { enUS, es, fr, de } from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { X, XCircleIcon } from "lucide-react";
import { agregarCita } from "../../slices/calendarSlice";

const fakeBackendData = {
  availableHours: {
    Monday: ["09:00-17:00"],
    Tuesday: ["09:00-17:00"],
    Wednesday: ["09:00-17:00"],
    Thursday: ["09:00-17:00"],
    Friday: ["09:00-17:00"],
    Saturday: ["09:00-13:15"],
    Sunday: [], // Unavailable
  },
  maxAppointmentTime: 60, // Max appointment duration in minutes
  maxAnticipatedDays: 30, // Max days in the future a user can book
};

export default function ModalCitasCliente({
  isModalOpen,
  setIsModalOpen,
  success,
  genericError,
}) {
  const dispatch = useDispatch();

  const disabledDates = ["2025-02-20", "2025-03-05"];

  const [selectedTime, setSelectedTime] = useState(null);
  const [citasAgendadas, setCitasAgendadas] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [disabledTimes, setDisabledTimes] = useState([]);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState("");
  const [filteredEndTimes, setFilteredEndTimes] = useState([]);
  const [eventName, setEventName] = useState("");
  const [events, setEvents] = useState([]);

  /* @description: Generate available dates for the calendar */
  useEffect(() => {
    const today = new Date();
    const maxDate = addDays(today, fakeBackendData.maxAnticipatedDays);
    let dates = [];

    for (let d = today; d <= maxDate; d = addDays(d, 1)) {
      const dayOfWeek = format(d, "EEEE");
      if (fakeBackendData.availableHours[dayOfWeek].length > 0) {
        dates.push({ date: format(d, "yyyy-MM-dd"), day: dayOfWeek });
      }
    }
    setAvailableDates(dates);
  }, []);

  /* @description: Get the correct day name in UTC, [Monday, Tuesday, ...] */
  const getCorrectDay = (dateStr) => {
    const selectedDate = parseISO(dateStr);
    return format(selectedDate, "EEEE");
  };

  /* @description: Handle date click event on the calendar */
  const handleDateClick = (arg) => {
    const selectedDay = getCorrectDay(arg.dateStr);
    if (fakeBackendData.availableHours[selectedDay].length === 0) {
      genericError("No hay horas disponibles para este día.");
      return;
    }

    setSelectedDate(arg.dateStr);
    setIsTimeModalOpen(true);
    setStartTime("");
    setSelectedEndTime("");

    const [start, end] =
      fakeBackendData.availableHours[selectedDay][0].split("-");
    setAvailableTimes(generateTimeSlots(start, end));
  };

  /* @description: Generate time slots between two given times */
  const generateTimeSlots = (start, end) => {
    let times = [];
    let current = parse(start, "HH:mm", new Date());
    let endTime = parse(end, "HH:mm", new Date());

    while (isBefore(current, endTime)) {
      times.push(format(current, "HH:mm"));
      current = addMinutes(current, 15);
    }
    return times;
  };

  /* @description: Filter end times based on the selected start time */
  const filterEndTimes = (startTime) => {
    const startIdx = availableTimes.indexOf(startTime);
    if (startIdx === -1) return [];
    return availableTimes.slice(startIdx + 1, startIdx + 5); // Show up to 60 min
  };

  const addMinutes = (date, minutes) =>
    new Date(date.getTime() + minutes * 60000);

  const handleStartTimeSelection = (time) => {
    if (startTime === time) {
      setStartTime("");
      setSelectedEndTime("");
      setFilteredEndTimes([]);
      return;
    }

    setStartTime(time);
    setSelectedEndTime("");
    setFilteredEndTimes(filterEndTimes(time));
  };

  /* @description: Save the appointment to the calendar */
  const handleSaveAppointment = () => {
    if (!eventName.trim()) {
      genericError("Por favor, ingrese un nombre de cita.");
      return;
    }

    if (!startTime || !selectedEndTime) {
      genericError("Por favor, seleccione una hora de inicio y fin válida.");
      return;
    }

    setEvents([
      ...events,
      { title: eventName, date: selectedDate, time: startTime },
    ]);
    setIsTimeModalOpen(false);
    setIsModalOpen(false);
    success();
    setEventName("");
  };

  /* @description: Close the time selection modal */
  const handleCloseTimeSeletionModal = () => {
    setIsTimeModalOpen(false);
    setEventName("");
    setStartTime("");
    setSelectedEndTime("");
    setFilteredEndTimes([]);
  };

  /* @description: Get the formatted schedule for the event */
  const getFormattedSchedule = (date, startTime, endTime, language = "en") => {
    const locales = { en: enUS, es: es, fr: fr, de: de };
    const formattedDate = format(parseISO(date), "EEEE, MMMM d, yyyy", {
      locale: locales[language] || enUS,
    });
    return `${formattedDate} / ${startTime} - ${endTime}`;
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-lg shadow-lg p-6 w-[90%] max-w-[1200px] h-[90vh] max-h-[700px] border border-gray-100"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
          >
            <div className="font-poppina relative w-full h-full bg-background dark:text-blue-500">
              <div className="pb-5">
                <X
                  onClick={() => setIsModalOpen(false)}
                  className="absolute right-0 cursor-pointer hover:scale-105 transition-all ease-out"
                />
              </div>
              <div className="h-[500px] mt-3 sm:h-[700px]"> {/* Contenedor con altura definida */}
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,dayGridWeek,dayGridDay",
                  }}
                  height={window.innerWidth < 640 ? "500px" : "620px"} 
                  
                  dateClick={handleDateClick}
                  events={events}
                  locale={esLocale}
                  dayCellClassNames={(arg) =>
                    disabledDates.includes(arg.date.toISOString().split("T")[0])
                      ? "fc-disabled-day"
                      : ""
                  }
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {isTimeModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-lg shadow-lg p-6 w-96 border border-gray-100"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
          >
            <div className="flex justify-between">
              <h2 className="text-lg font-semibold mb-4 text-gray-800">
                Selecciona la hora
              </h2>
              <button
                className="text-gray-600 hover:text-red-600"
                onClick={() => handleCloseTimeSeletionModal()}
              >
                <XCircleIcon size={24} />
              </button>
            </div>

            <input
              type="text"
              placeholder="Nombre de la cita"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-2"
            />
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm font-semibold text-gray-600">Fecha</h2>
              <span className="text-sm text-gray-500">
                {getFormattedSchedule(
                  selectedDate,
                  startTime,
                  selectedEndTime,
                  "es"
                )}
              </span>
            </div>

            <h2 className="text-sm font-semibold mb-2 text-gray-600">
              Selecciona Hora de Inicio
            </h2>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <AnimatePresence>
                {availableTimes.map(
                  (time) =>
                    (startTime === "" || startTime === time) && (
                      <motion.button
                        key={time}
                        className={`p-2 rounded-md text-white hover:bg-blue-600 ${
                          startTime === time
                            ? "bg-blue-700 border-2 border-blue-900 shadow-md"
                            : "bg-blue-500"
                        }`}
                        onClick={() => handleStartTimeSelection(time)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {time}
                      </motion.button>
                    )
                )}
              </AnimatePresence>
            </div>

            {startTime && (
              <>
                <h2 className="text-sm font-semibold mb-2 text-gray-600">
                  Seleccione Hora de Fin
                </h2>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <AnimatePresence>
                    {filteredEndTimes.map((time) => (
                      <motion.button
                        key={time}
                        className={`p-2 rounded-md text-white hover:bg-indigo-600 ${
                          selectedEndTime === time
                            ? "bg-indigo-700 border-2 border-indigo-900 shadow-md"
                            : "bg-indigo-500"
                        }`}
                        onClick={() => setSelectedEndTime(time)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {time}
                      </motion.button>
                    ))}
                  </AnimatePresence>
                </div>
              </>
            )}

            <button
              className="w-full bg-green-500 text-white py-2 rounded hover:bg-green-600"
              onClick={handleSaveAppointment}
            >
              Confirmar Cita
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}